<template>
  <div>
    <MessageAlert ref="messageAlert" />
    <div>
      <!-- Wrap both the back button and the tab buttons in a flex container -->
      <div class="tab-header">
        <button class="back-button" @click="$router.back()">
          <i class="fas fa-chevron-left"></i>
          {{ $t('MaintenanceDeviceInfo.back_to_device_list') }}
        </button>

        <div class="device-info">
          <span class="mac-sn-text">{{ device.MAC_HEX }}</span>
          <span class="divider">•</span>
          <span class="mac-sn-text">{{ device.SN }}</span>
        </div>

        <!-- Tab buttons -->
        <div class="tab">
          <button class="tablinks" :class="{ activeLog: activeTab === 'Logs' }" @click="activeTab = 'Logs'"><i class="fas fa-file-alt"></i> {{ $t('MaintenanceDeviceInfo.logs') }}</button>
          <button class="tablinks" :class="{ activeError: activeTab === 'Errors' }" @click="activeTab = 'Errors'"><i class="fas fa-exclamation-triangle"></i> {{ $t('MaintenanceDeviceInfo.errors') }}</button>
          <button class="tablinks" :class="{ activeRepair: activeTab === 'Repair' }" @click="activeTab = 'Repair'"><i class="fas fa-tools"></i> {{ $t('MaintenanceDeviceInfo.maintenance_data') }}</button>
          <button class="tablinks" :class="{ activeSettings: activeTab === 'Settings' }" @click="activeTab = 'Settings'"><i class="fas fa-cog"></i> {{ $t('MaintenanceDeviceInfo.settings') }}</button>
            
          <div>
            <!-- Firmware Update Button -->
            <button @click="triggerFWOTAUpdateCallback(device.MAC)" v-if="updateAvailable"
              class="icon-button firmware" title="Update firmware">
              <i class="fas fa-download"></i>
            </button>

            <!-- Disable Maintenance Mode Button -->
            <button v-if="isInMaintenanceMode(device.MAC)"
              @click="disableMaintenanceMode(device.MAC)" class="icon-button maintenance disable"
              title="Disable Maintenance Mode">
              <i class="fas fa-wrench"></i>
            </button>

            <!-- Enable Maintenance Mode Button -->
            <button v-if="!isInMaintenanceMode(device.MAC)"
              @click="enableMaintenanceMode(device.MAC)" class="icon-button maintenance enable"
              title="Enable Maintenance Mode">
              <i class="fas fa-wrench"></i>
            </button>
          </div>
        </div>
      </div>

      <!-- Tab content -->
      <div class="tab-container">
        <div v-if="activeTab === 'Logs'" class="tabcontent">
          <MaintenanceDeviceLogs :deviceId="id" @showError="showError" @showSuccess="showSuccess" />
        </div>

        <div v-if="activeTab === 'Errors'" class="tabcontent">
          <MaintenanceDeviceErrors :deviceId="id" @showError="showError" @showSuccess="showSuccess" />
        </div>

        <div v-if="activeTab === 'Repair'" class="tabcontent">
          <MaintenanceDeviceRepairData :deviceId="id" :isInMaintenanceMode="isInMaintenanceMode(device.MAC)" @showError="showError" @showSuccess="showSuccess" />
        </div>

        <div v-if="activeTab === 'Settings'" class="tabcontent">
          <MaintenanceDeviceSettingsData :deviceId="id" :macAddress="device.MAC"
            @showError="showError" @showSuccess="showSuccess" />
        </div>
      </div>
    </div>
  </div>
</template>
<style scoped>
.tab-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 20px;
  width: 90%;
  margin: 20px auto;
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 10px;
}
.tab-container {
  width: 90%;
  margin: 0 auto;
  padding: 0 20px;
}
.back-button {
  display: inline-flex;
  align-items: center;
  padding: 10px 20px;
  font-size: 16px;
  color: #fff;
  background-color: #3498db;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  transition: background-color 0.3s ease;
  text-transform: uppercase;
}

.back-button i {
  margin-right: 8px;
  /* Spacing between icon and text */
}

.back-button:hover {
  background-color: #2980b9;
}

.back-button:focus {
  outline: none;
}

.back-button:active {
  background-color: #1c6e8e;
}

.tab {
  display: flex;
  gap: 10px;
  /* Space between the tab buttons */
}

.tablinks {
  padding: 10px 20px;
  cursor: pointer;
  border: none;
  background-color: #f1f1f1;
  border-bottom: 2px solid transparent;
  text-transform: uppercase;
}

.tablinks.activeLog {
  border-bottom: 2px solid #3498db;
  font-weight: bold;
}

.tablinks.activeError {
  border-bottom: 2px solid #e74c3c;
  font-weight: bold;
}

.tablinks.activeRepair {
  border-bottom: 2px solid #ffa500;
  font-weight: bold;
}

.tablinks.activeSettings {
  border-bottom: 2px solid #179b0b;
  font-weight: bold;
}

.tabcontent {
  width: 100%;
  padding: 0;
  margin-top: 20px;
  box-sizing: border-box;
}

/* Button styling */
.icon-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background-color: #179b0b;
    color: white;
    border: none;
    border-radius: 5px;
    cursor: pointer;
    font-size: 14px;
    padding: 10px 20px;
    margin: 5px;
    transition: background-color 0.3s ease;
}

/* Hover effect for buttons */
.icon-button:hover {
    background-color: #14810a;
}

/* Maintenance button specific styles */
.icon-button.maintenance.enable {
    background-color: orange;
}

.icon-button.maintenance.disable {
    background-color: red;
}

/* Hover effect for maintenance buttons */
.icon-button.maintenance.enable:hover {
    background-color: darkorange;
}

.icon-button.maintenance.disable:hover {
    background-color: darkred;
}
.device-info {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 18px;
  font-weight: 500;
  background-color: #eef2f7;
  padding: 10px 16px;
  border-radius: 8px;
  box-shadow: inset 0 1px 3px rgba(0,0,0,0.06);
}

.device-info i {
  color: #0f62fe;
  font-size: 20px;
}

.mac-sn-text {
  font-family: monospace;
  color: #2c3e50;
}

.divider {
  font-size: 20px;
  color: #aaa;
}
</style>
  

<script>
import { backendUrl } from '../config/envVariablesConfig';
import axios from 'axios';
import keycloak from '@/keycloak';
import MessageAlert from '@/components/MessageAlert.vue';
import MaintenanceDeviceLogs from '@/components/MaintenanceDeviceLogs.vue';
import MaintenanceDeviceErrors from '@/components/MaintenanceDeviceErrors.vue';
import MaintenanceDeviceRepairData from '@/components/MaintenanceDeviceRepairData.vue';
import MaintenanceDeviceSettingsData from '@/components/MaintenanceDeviceSettingsData.vue'

export default {
  props: {
    id: {
      type: String,
      required: true
    }
  },
  components: {
    MessageAlert,
    MaintenanceDeviceLogs,
    MaintenanceDeviceErrors,
    MaintenanceDeviceRepairData,
    MaintenanceDeviceSettingsData
  },
  data() {
    return {
      device: {
        id: this.id,
        SN: '',
        MAC: ''
      },
      updateAvailable: true,
      devicesSetToMaintenanceMode: {}, // Store device_ids set to maintenance mode
      activeTab: 'Logs', // Default tab when component mounts
      userData: {},
      flags: {},
      selectedLanguage: 'en'
    };
  },
  async mounted() {
    window.addEventListener('beforeunload', this.handleBeforeUnload);
    await this.fetchDeviceData(this.id);
  },
  beforeUnmount() {
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
  },
  methods: {
    showError(message) {
      this.$emit('showError', message);
    },
    showSuccess(message) {
      this.$emit('showSuccess', message);
    },
    // Event that will trigger when users closes tab to put device back to regular mode
    async handleBeforeUnload(event) {
      await this.setAllDevicesBackToRegularMode();
      event.returnValue = ''; // Some browsers show a dialog if returnValue is set
    },
    async triggerFWOTAUpdateCallback(mac_address) {
      const confirmed = window.confirm(this.$t('MaintenanceDeviceInfo.confirm_ota'));
      // If the user confirms the removal
      if (confirmed) {
        try {
          // Call the async removeDevice function
          await this.triggerFirmwareUpdate(mac_address);
        } catch (error) {
          console.error('Error updating device:', error);
          alert(this.$t('MaintenanceDeviceInfo.failed_update_device'));
        }
      }
    },
    /**
     * Send device a message to trigger update
     *
     * @returns {Promise<void>} A promise that resolves when the data is fetched.
     * @throws {Error} Throws an error if the device ID is invalid or if fetching fails.
     */
    async triggerFirmwareUpdate(mac_address) {
      try {
        const response = await axios.post(
          `${backendUrl}/api/device/otaupdate`,
          { mac: mac_address },
          { 
              headers: {
                Authorization: `Bearer ${keycloak.token}`
              },
          }
        );


        // check is request returned any error
        if (response.status !== 200) {
          this.showError(response.data.error);
          return;
        }

        // Return the fetched logs data
        this.showSuccess(this.$t('MaintenanceDeviceInfo.update_triggered'));
      } catch (error) {
        console.error(`triggerFirmwareUpdate: ${error}`);
        this.showError(this.$t('MaintenanceDeviceInfo.response_internal_error'));
      }
    },
    async fetchDeviceData(device_id){
      
      try {
            const response = await axios.get(
            `${backendUrl}/api/device`,
            {
              params: { id: device_id },
              headers: {
                Authorization: `Bearer ${keycloak.token}`
              }
            });

            // check is request returned any error
            if (response.status !== 200) {
            this.showError(response.data.error);
            return;
            }
            // validate response data
            if (!response.data || !response.data.data) {
            this.showError(this.$t('MainPage.response_invalid_structure'));
            return;
            }
            if (!response.data.success) {
            this.showError(this.$t('MainPage.response_with_error'));
            return;
            }

            this.device = response.data.data;
            // Ensure you're accessing the correct data path for language in the response
            this.showSuccess(`${this.$t('MainPage.hello_text')} ${this.userData.firstName}`);

            return;
        }
        catch (error) {
            console.error(`fetchDeviceAndOwnerData: ${error}`);
            this.showError(this.$t('MainPage.response_internal_error'));
            return;
        }
    },
    //#region Change maintenance mode        
    isInMaintenanceMode(mac_address) {
      // Check if the mac_address is in the devicesSetToMaintenanceMode object
      return this.devicesSetToMaintenanceMode[mac_address] === true;
    },
    async enableMaintenanceMode(mac_address) {
      // Directly set the mac_address to true in the devicesSetToMaintenanceMode object
      this.devicesSetToMaintenanceMode[mac_address] = true;
      await this.changeMaintenanceMode(mac_address, true); // Change to maintenance mode
    },
    async disableMaintenanceMode(mac_address) {
      // Directly delete the mac_address from the devicesSetToMaintenanceMode object
      delete this.devicesSetToMaintenanceMode[mac_address];
      await this.changeMaintenanceMode(mac_address, false); // Change out of maintenance mode
    },
    async setAllDevicesBackToRegularMode() {
      // on page close if any of devices are left in repair mode, this will reset them back to regular mode
      for (const mac_address in this.devicesSetToMaintenanceMode) {
        console.info(`Set device ${mac_address} back to regular data`);
        await this.changeMaintenanceMode(mac_address, false);
      }
    },
    /**
     * Change device mode to test/maintenance
     *
     * @returns {Promise<void>} A promise that resolves when the data is fetched.
     * @throws {Error} Throws an error if the device ID is invalid or if fetching fails.
     */
    async changeMaintenanceMode(mac_address, maintenanceMode) {
      try {
        const response = await axios.post(
          `${backendUrl}/api/device/maintenancemode`,
          {
            mac: mac_address,
            repair_mode: maintenanceMode
          },
          { 
            headers: {
              Authorization: `Bearer ${keycloak.token}`
            }
          });

        
        // check is request returned any error
        if (response.status !== 200) {
          this.showError(response.data.error);
          return;
        }
      } catch (error) {
        console.error(`changeMaintenanceMode: ${error}`);
        //this.showError(this.$t('general_errors.response_internal_error'));
      }
    }
    //#endregion
  }
};
</script>
